import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useState } from 'react';

import { ShimmerCollapsibleHeader } from '@/components/blocks/Message/reasoning/ShimmerCollapsibleHeader';
import { ModalsManager } from '@/components/modals/modalsManager';
import { YStack } from '@/components/ui/layout/layout';
import { Text } from '@/components/ui/typography';
import { FeedbackModal } from '@/features/feedback/feedback';
import { Feedback } from '@/features/feedback/feedback.types';

const TRIGGERED_TITLE = 'This conversation is being compacted';
const TRIGGERED_BODY =
  'Earlier messages are being compacted to maintain performance.';
const APPLIED_TITLE = 'This conversation was compacted';
const APPLIED_BODY = 'Earlier messages were compacted to maintain performance.';
const FEEDBACK_LINK_LABEL = 'If you notice any issues, please send feedback.';

const buildCompactionFeedbackPrefill = (threadId: string): string =>
  `Compaction feedback

Chat ID: ${threadId}

Please describe what looked wrong after this conversation was summarized:

`;

type CompactionBannerStatus = 'triggered' | 'applied';

type CompactionBannerProps = {
  status: CompactionBannerStatus;
  threadId: string;
  summary?: string | null;
};

export const CompactionBanner = ({
  status,
  threadId,
  summary,
}: CompactionBannerProps) => {
  const isTriggered = status === 'triggered';
  const title = isTriggered ? TRIGGERED_TITLE : APPLIED_TITLE;
  const body = summary ?? (isTriggered ? TRIGGERED_BODY : APPLIED_BODY);
  const [isOpen, setIsOpen] = useState(false);

  const openFeedbackModal = () =>
    ModalsManager.show(FeedbackModal, {
      initialFeedbackType: Feedback.Bug,
      initialFeedback: buildCompactionFeedbackPrefill(threadId),
    });

  return (
    <YStack
      className="w-full px-6 py-3"
      aria-live={isTriggered ? 'polite' : undefined}
    >
      <ShimmerCollapsibleHeader
        title={title}
        isShimmering={isTriggered}
        open={isOpen}
        onOpenChange={setIsOpen}
      />
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <blockquote className="border-l-2 border-[var(--reasoning-accent)] pl-3 mt-2 mx-1">
              <Text size="sm" variant="muted">
                {body}
              </Text>
            </blockquote>
            {!isTriggered && (
              <Text size="sm" variant="muted" className="px-1 pt-1">
                <FeedbackLinkButton onClick={openFeedbackModal}>
                  {FEEDBACK_LINK_LABEL}
                </FeedbackLinkButton>
              </Text>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </YStack>
  );
};

type FeedbackLinkButtonProps = {
  onClick: () => void;
  children: ReactNode;
};

// Inline link inside a paragraph: rendered as a raw <button> because the
// shared Button component is display:grid and cannot flow with surrounding
// text. Styled as a textual link to match the rest of the banner copy.
const FeedbackLinkButton = ({ onClick, children }: FeedbackLinkButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className="text-[var(--reasoning-accent)] underline underline-offset-2 hover:opacity-80 transition-opacity"
  >
    {children}
  </button>
);
